import React from 'react';
import { Box, Heading, SideSheet, SideSheetProps, useSnackbar } from 'pounceV2';
import { extractErrorMessage } from 'Helpers/utils';
import UserFormV3, { UserFormValues } from 'Components/forms/UserForm/UserFormV3';
import useAuth from 'Hooks/useAuth';
import { UserDetails } from 'Source/graphql/fragments/UserDetails.generated';
import { UserRoleInputKind } from 'Generated/schema';
import { useEditUser } from './graphql/editUser.generated';

export interface EditUserSidesheetProps extends SideSheetProps {
  user: UserDetails;
}

const EditUserSidesheetV3: React.FC<EditUserSidesheetProps> = ({ user, onClose, ...rest }) => {
  const { pushSnackbar } = useSnackbar();
  const { refetchUserInfo, userInfo } = useAuth();
  const [editUser] = useEditUser({
    onError: error => pushSnackbar({ variant: 'error', title: extractErrorMessage(error) }),
    onCompleted: () => {
      onClose();

      // Refetch user info if editing self
      if (user.id === userInfo.id) {
        refetchUserInfo();
      }

      pushSnackbar({ variant: 'success', title: 'User updated successfully!' });
    },
  });

  const initialValues = {
    id: user.id,
    email: user.email,
    familyName: user.familyName || '',
    givenName: user.givenName || '',
    role: user.role,
  };

  const submitToServer = async (values: UserFormValues) => {
    await editUser({
      variables: {
        input: {
          id: values.id,
          email: values.email || null,
          familyName: values.familyName || null,
          givenName: values.givenName || null,
          role: { kind: UserRoleInputKind.Id, value: values.role.id },
        },
      },
    });
  };

  return (
    <SideSheet aria-labelledby="sidesheet-title" onClose={onClose} {...rest}>
      <Box width={600} m="auto">
        <Heading variant="h5" fontWeight="bold" mb={9} id="sidesheet-title">
          Edit Profile
        </Heading>
        <UserFormV3 initialValues={initialValues} onSubmit={submitToServer} />
      </Box>
    </SideSheet>
  );
};

export default EditUserSidesheetV3;
